import { Client } from '../types';
import { FilterOptions } from '../components/ClientFilter';

/**
 * Check if a case has been active for at least the specified number of days
 */
export const isAtLeastDaysOld = (dateString: string, days: number): boolean => {
  const applicationDate = new Date(dateString);
  const today = new Date();
  const diffTime = today.getTime() - applicationDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= days;
};

/**
 * Check if a date is within a custom date range
 */
export const isDateWithinCustomRange = (
  dateString: string, 
  startDate?: string, 
  endDate?: string
): boolean => {
  const checkDate = new Date(dateString);
  
  if (startDate) {
    const start = new Date(startDate);
    if (checkDate < start) {
      return false;
    }
  }
  
  if (endDate) {
    const end = new Date(endDate);
    // Set end date to end of day for inclusive comparison
    end.setHours(23, 59, 59, 999);
    if (checkDate > end) {
      return false;
    }
  }
  
  return true;
};

/**
 * Check if a client has any fingerprints taken
 */
export const hasFingerprints = (client: Client): boolean => {
  return client.cases.some(caseItem => caseItem.biometricTaken);
};

/**
 * Get the most recent application date from all client cases
 */
export const getMostRecentApplicationDate = (client: Client): string | null => {
  if (client.cases.length === 0) return null;
  
  const dates = client.cases
    .map(caseItem => caseItem.applicationDate)
    .filter(date => date && date.trim() !== '')
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
  
  return dates.length > 0 ? dates[0] : null;
};

/**
 * Filter clients based on the provided filter options
 */
export const filterClients = (clients: Client[], filters: FilterOptions): Client[] => {
  return clients.filter(client => {
    // Archive status filter (applied first)
    if (filters.archiveStatus === 'active' && client.isArchived) {
      return false;
    }
    if (filters.archiveStatus === 'archived' && !client.isArchived) {
      return false;
    }

    // Date range filter
    if (filters.dateRange !== 'all') {
      const mostRecentDate = getMostRecentApplicationDate(client);
      if (!mostRecentDate) {
        return false; // Exclude clients with no valid application dates
      }
      
      if (filters.dateRange === 'custom') {
        // Use custom date range
        if (!isDateWithinCustomRange(mostRecentDate, filters.customStartDate, filters.customEndDate)) {
          return false;
        }
      } else {
        // Use predefined day ranges - check if case is at least X days old
        const days = parseInt(filters.dateRange);
        if (!isAtLeastDaysOld(mostRecentDate, days)) {
          return false;
        }
      }
    }

    // Biometric status filter
    if (filters.biometricStatus !== 'all') {
      const clientHasFingerprints = hasFingerprints(client);

      if (filters.biometricStatus === 'taken' && !clientHasFingerprints) {
        return false;
      }

      if (filters.biometricStatus === 'pending' && clientHasFingerprints) {
        return false;
      }
    }

    // Country filter
    if (filters.country && filters.country !== 'all') {
      const clientCountry = String(client.countryOfOrigin || '').toLowerCase();
      if (clientCountry !== filters.country.toLowerCase()) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Get filter statistics for display
 */
export const getFilterStats = (allClients: Client[], filteredClients: Client[]) => {
  const totalClients = allClients.length;
  const filteredCount = filteredClients.length;
  const withFingerprints = allClients.filter(hasFingerprints).length;
  const withoutFingerprints = totalClients - withFingerprints;
  
  return {
    total: totalClients,
    filtered: filteredCount,
    withFingerprints,
    withoutFingerprints,
    filteredPercentage: totalClients > 0 ? Math.round((filteredCount / totalClients) * 100) : 0,
  };
};
